import { toast } from '@heroui/react';
import { useState, useMemo } from 'react';

import type { SortingState, PaginationState } from '@hs/ui';
import { AlertDialog, Button, Dropdown, Label } from '@heroui/react';
import {
  IconPlus as Plus,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { AiBillingRuleEditorModal } from './AiBillingRuleEditorModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import {
  AI_BILLING_CURRENCY_LABELS,
  AI_BILLING_DIMENSION_LABELS,
  AI_BILLING_UNIT_LABELS,
  formatBillingRuleInputParametersSummary,
  type AiBillingCurrency,
  type AiBillingDimension,
  type AiBillingUnit,
} from '../constants';
import type { BillingRuleLineFormValues } from './AiBillingRuleLinesEditor';

type AiBillingRule = {
  id: string;
  modelId?: string | null;
  version?: number | null;
  rule?: BillingRuleLineFormValues[] | null;
  currency?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  remark?: string | null;
  createdAt?: string | null;
};

function BillingRuleLinesGrid({ items }: { items: BillingRuleLineFormValues[] }) {
  if (!items.length) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="min-w-[36rem] py-1">
      <div className="grid grid-cols-5 gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>参数规格</span>
        <span>计价维度</span>
        <span>计价单位</span>
        <span>成本价</span>
        <span>售价</span>
      </div>
      {items.map((item, index) => (
        <div
          key={`${item.usage}-${item.unit}-${index}`}
          className="grid grid-cols-5 gap-x-3 gap-y-1 text-sm"
        >
          <span className="text-muted-foreground">
            {formatBillingRuleInputParametersSummary(item.inputParameters)}
          </span>
          <span>{AI_BILLING_DIMENSION_LABELS[item.usage as AiBillingDimension] ?? item.usage}</span>
          <span>{AI_BILLING_UNIT_LABELS[item.unit as AiBillingUnit] ?? item.unit}</span>
          <span>{item.costPrice}</span>
          <span>{item.price}</span>
        </div>
      ))}
    </div>
  );
}

export function AiBillingRulesManagement() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AiBillingRule | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<AiBillingRule | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];

  const { data: modelsData } = trpc.aiModel.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated }
  );

  const modelLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of modelsData ?? []) {
      const row = m as {
        id?: string;
        modelName?: string | null;
        modelCode?: string | null;
        modelVersion?: string | null;
      };
      if (!row.id) continue;
      const name = row.modelName || row.modelCode || row.id;
      const ver = row.modelVersion ? ` (${row.modelVersion})` : '';
      map.set(row.id, `${name}${ver}`);
    }
    return map;
  }, [modelsData]);

  const { data, isLoading, refetch } = trpc.aiBillingRule.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortItem
        ? { sortBy: sortItem.id, sortOrder: sortItem.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const rules = (data?.items ?? []) as AiBillingRule[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteRuleMutation = trpc.aiBillingRule.delete.useMutation({
    onSuccess: () => {
      toast.success('计费规则删除成功');
      setDeletingRuleId(null);
      setDeleteModalOpen(false);
      setRuleToDelete(null);
      void utils.aiBillingRule.list.invalidate();
      void utils.aiBillingRule.listAll.invalidate();
    },
    onSettled: () => {
      setDeletingRuleId(null);
    },
  });

  const columnOverrides = useMemo(
    () => ({
      modelId: {
        cell: (row: AiBillingRule) => {
          const id = row.modelId;
          if (!id) return '—';
          return modelLabelMap.get(id) ?? id;
        },
      },
      version: {
        cell: (row: AiBillingRule) => {
          const v = row.version;
          return v != null ? `v${String(v)}` : '—';
        },
      },
      rule: {
        cell: (row: AiBillingRule) => {
          const items = Array.isArray(row.rule) ? row.rule : [];
          return <BillingRuleLinesGrid items={items} />;
        },
      },
      currency: {
        cell: (row: AiBillingRule) => {
          const c = row.currency as AiBillingCurrency | null | undefined;
          if (!c) return '—';
          return AI_BILLING_CURRENCY_LABELS[c] ?? c;
        },
      },
      effectiveTo: {
        cell: (row: AiBillingRule) => row.effectiveTo || '—',
      },
      remark: {
        cell: (row: AiBillingRule) => row.remark || '—',
      },
    }),
    [modelLabelMap]
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<AiBillingRule>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="ai_billing_rule"
          data={rules}
          recordCount={total}
          pageCount={pageCount}
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          columnOverrides={columnOverrides}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onPress={() => {
                  setEditingRule(null);
                  setEditorModalOpen(true);
                }}
              >
                <Plus className="size-4" stroke={1.5} />
                创建
              </Button>
              <Button variant="secondary" onPress={() => void refetch()}>
                <RefreshCw className="size-4" stroke={1.5} />
                刷新
              </Button>
            </div>
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<AiBillingRule>>
            {({ row }) => {
              const rule = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => {
                            setEditingRule(rule);
                            setEditorModalOpen(true);
                          }}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete"
                          textValue="删除"
                          variant="danger"
                          onAction={() => {
                            setRuleToDelete(rule);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <AiBillingRuleEditorModal
        open={editorModalOpen}
        rule={editingRule}
        onCancel={() => {
          setEditorModalOpen(false);
          setEditingRule(null);
        }}
        onSuccess={() => {
          setEditorModalOpen(false);
          setEditingRule(null);
          void refetch();
        }}
      />

      <AlertDialog isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除该计费规则（版本{' '}
                {ruleToDelete?.version != null ? `v${ruleToDelete.version}` : '—'}）吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setRuleToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletingRuleId === ruleToDelete?.id}
                  onPress={() => {
                    if (!ruleToDelete) return;
                    setDeletingRuleId(ruleToDelete.id);
                    void deleteRuleMutation.mutateAsync({ id: ruleToDelete.id });
                  }}
                >
                  {deletingRuleId === ruleToDelete?.id ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
